import { useEffect, useState } from "react";
import ParticipantView from "./views/ParticipantView";
import ManualJoinView from "./views/ManualJoinView";
import StageView from "./views/StageView";
import InsightsView from "./views/InsightsView";
import FacilitatorView from "./views/FacilitatorView";
import EventsView from "./views/EventsView";
import LoginView from "./views/LoginView";
import ResetPasswordView from "./views/ResetPasswordView";
import AuthErrorView from "./views/AuthErrorView";
import { LegalView } from "./views/LegalView";
import { useAuth } from "./hooks/useAuth";
import type { Event } from "./lib/types";
import { Sentry } from "./lib/telemetry";

type Route =
  | { type: "home" }
  | { type: "participate"; joinCode: string; streamCode: string | null }
  | { type: "join_manual" }
  | { type: "stage"; joinCode: string }
  | { type: "insights"; joinCode: string; embed: boolean }
  | { type: "facilitator" }
  | { type: "reset_password" }
  | { type: "auth_error"; code: string | null; description: string | null }
  | { type: "impressum" }
  | { type: "datenschutz" };

function getRoute(): Route {
  const path = window.location.pathname.replace(/\/$/, "");
  const search = window.location.search;

  // Supabase appends auth failures to the URL *hash* (e.g. an expired signup or
  // recovery link → #error=access_denied&error_code=otp_expired&...). Handle it
  // before path routing so the user sees a message instead of a silent home page.
  const hash = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : "";
  if (hash.includes("error=")) {
    const hp = new URLSearchParams(hash);
    return {
      type: "auth_error",
      code: hp.get("error_code"),
      description: hp.get("error_description"),
    };
  }

  const joinMatch = path.match(/^\/join\/([A-Z0-9]{4,6})(?:\/([A-Z0-9]{4,6}))?$/i);
  const stageMatch = path.match(/^\/stage\/([A-Z0-9]{4,6})$/i);
  const insightsMatch = path.match(/^\/insights\/([A-Z0-9]{4,6})$/i);
  if (joinMatch) {
    return {
      type: "participate",
      joinCode: joinMatch[1].toUpperCase(),
      streamCode: joinMatch[2] ? joinMatch[2].toUpperCase() : null,
    };
  }
  if (path === "/join") return { type: "join_manual" };
  if (stageMatch) return { type: "stage", joinCode: stageMatch[1].toUpperCase() };
  if (insightsMatch) {
    const embed = new URLSearchParams(search).get("embed") === "1";
    return { type: "insights", joinCode: insightsMatch[1].toUpperCase(), embed };
  }
  if (path === "/facilitator") return { type: "facilitator" };
  if (path === "/reset-password") return { type: "reset_password" };
  if (path === "/impressum") return { type: "impressum" };
  if (path === "/datenschutz") return { type: "datenschutz" };
  return { type: "home" };
}

const route = getRoute();

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<CrashScreen />}>
      <AppRoutes />
    </Sentry.ErrorBoundary>
  );
}

function AppRoutes() {
  const { user, session, loading } = useAuth();
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  useEffect(() => {
    document.title = "REASONZ · Das Echtzeit-Insight-System von READYCAL";
  }, []);

  if (route.type === "participate") {
    return <ParticipantView joinCode={route.joinCode} streamCode={route.streamCode} />;
  }

  if (route.type === "join_manual") {
    return <ManualJoinView />;
  }

  if (route.type === "stage") {
    return <StageView joinCode={route.joinCode} />;
  }

  if (route.type === "insights") {
    return <InsightsView joinCode={route.joinCode} embed={route.embed} />;
  }

  if (route.type === "auth_error") {
    return <AuthErrorView code={route.code} description={route.description} />;
  }

  if (route.type === "reset_password") {
    return <ResetPasswordView />;
  }

  if (route.type === "impressum") {
    return <LegalView page="impressum" />;
  }

  if (route.type === "datenschutz") {
    return <LegalView page="datenschutz" />;
  }

  if (route.type === "facilitator") {
    if (loading) return <LoadingScreen />;
    if (!user || !session) return <LoginView />;
    if (selectedEvent) {
      return (
        <FacilitatorView
          event={selectedEvent}
          accessToken={session.access_token}
          onBack={() => setSelectedEvent(null)}
        />
      );
    }
    return <EventsView onSelectEvent={setSelectedEvent} />;
  }

  return <LandingPage />;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-rc-cream flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-rc-fog border-t-rc-berry rounded-full animate-spin" />
    </div>
  );
}

function CrashScreen() {
  return (
    <div className="min-h-screen bg-rc-cream text-rc-black font-sans flex items-center justify-center px-6">
      <div className="max-w-md text-center">
        <div className="text-eyebrow font-semibold uppercase text-rc-charcoal mb-5">
          Fehler
        </div>
        <h1 className="font-display font-bold text-display-md tracking-display text-rc-black mb-4">
          Etwas ist schiefgelaufen.
        </h1>
        <p className="text-[16px] leading-[1.6] text-rc-charcoal mb-8">
          Wir haben den Fehler bekommen. Lade die Seite neu, um weiterzumachen.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center justify-center gap-2 bg-rc-berry text-rc-paper rounded-pill px-7 py-3.5 text-[15px] font-semibold hover:bg-rc-berry-700 active:scale-[0.97] transition ease-rc-out"
        >
          Seite neu laden
        </button>
      </div>
    </div>
  );
}

function LandingPage() {
  return (
    <div className="min-h-screen bg-rc-cream text-rc-black font-sans antialiased selection:bg-rc-lime selection:text-rc-black">
      <SiteHeader />
      <main id="main">
        <Hero />
        <Vocabulary />
        <Contrast />
        <UseCases />
        <Pricing />
        <Paths />
      </main>
      <SiteFooter />
    </div>
  );
}

function SiteHeader() {
  return (
    <header
      className="sticky top-0 z-50 border-b border-rc-fog"
      style={{
        background: "rgba(250, 248, 244, 0.86)",
        backdropFilter: "saturate(140%) blur(10px)",
        WebkitBackdropFilter: "saturate(140%) blur(10px)",
      }}
    >
      <div className="max-w-marketing mx-auto px-6 sm:px-8 py-4 flex items-center gap-8">
        <a href="/" aria-label="REASONZ" className="inline-flex items-baseline gap-2.5">
          <span className="font-display text-[22px] font-bold tracking-[-0.02em] text-rc-black">
            REASONZ<span className="text-rc-berry">.</span>
          </span>
          <span className="hidden sm:inline-block text-[11px] font-semibold uppercase tracking-[0.12em] text-rc-stone">
            by READYCAL
          </span>
        </a>
        <nav className="hidden md:flex gap-7 ml-2 text-[14px] font-medium text-rc-black/85">
          <a href="#vokabular" className="hover:text-rc-berry transition-colors">Was die KI sieht</a>
          <a href="#raeume" className="hover:text-rc-berry transition-colors">Drei Räume</a>
          <a href="#preise" className="hover:text-rc-berry transition-colors">Preise</a>
          <a href="#paths" className="hover:text-rc-berry transition-colors">Beitreten</a>
        </nav>
        <div className="ml-auto flex items-center gap-3">
          <a
            href="/facilitator"
            className="inline-flex items-center gap-2 bg-rc-black text-rc-paper rounded-pill px-5 py-2.5 text-[14px] font-semibold hover:bg-rc-graphite transition-colors ease-rc-out"
          >
            Console <span aria-hidden>→</span>
          </a>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="max-w-marketing mx-auto px-6 sm:px-8 pt-6 sm:pt-12 pb-14 sm:pb-20">
      <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-12 items-start">
        <div className="animate-fade-up">
          <div className="text-eyebrow font-semibold uppercase text-rc-charcoal mb-6">
            Der KI-Co-Moderator für Workshops
          </div>
          <h1
            className="font-display font-bold text-display-xl tracking-display text-rc-black mb-7 text-balance"
            style={{ hyphens: "auto", WebkitHyphens: "auto" }}
          >
            <span className="block">Frag einmal.</span>
            <span className="block text-rc-berry">Sieh den Raum denken.</span>
          </h1>
          <p className="text-[17px] sm:text-[18px] leading-[1.6] text-rc-charcoal max-w-[34rem] mb-7">
            Jede Antwort wird live in einen <strong className="font-semibold text-rc-black">Cluster</strong> einsortiert,
            sobald sie eintrifft. Was niemand gesagt hat, kommt als <strong className="font-semibold text-rc-black">Blind
            Spot</strong> aufs Plakat. Am Ende steht ein <strong className="font-semibold text-rc-black">Manifest</strong> des Raums.
          </p>
          <div className="flex flex-wrap items-center gap-2.5 mb-9">
            <span className="inline-flex items-center gap-1.5 rounded-pill border-[1.5px] border-rc-fog bg-rc-paper px-3 py-1 text-[12px] font-semibold uppercase tracking-[0.12em] text-rc-charcoal">
              <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full bg-rc-berry" />
              auf Deutsch
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-pill border-[1.5px] border-rc-fog bg-rc-paper px-3 py-1 text-[12px] font-semibold uppercase tracking-[0.12em] text-rc-charcoal">
              Anonym
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-pill border-[1.5px] border-rc-fog bg-rc-paper px-3 py-1 text-[12px] font-semibold uppercase tracking-[0.12em] text-rc-charcoal">
              Ohne App
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <a
              href="/facilitator"
              className="inline-flex items-center gap-2 bg-rc-black text-rc-paper rounded-pill px-7 py-3.5 text-[15px] font-semibold hover:bg-rc-graphite active:scale-[0.97] transition ease-rc-out"
            >
              Zur Facilitator Console <span aria-hidden>→</span>
            </a>
            <a
              href="#vokabular"
              className="inline-flex items-center gap-2 bg-transparent text-rc-black border-[1.5px] border-rc-black rounded-pill px-6 py-3 text-[15px] font-semibold hover:bg-rc-black hover:text-rc-paper active:scale-[0.97] transition ease-rc-out"
            >
              Was die KI sieht
            </a>
          </div>
        </div>

        <HeroAnchor />
      </div>
    </section>
  );
}

function HeroAnchor() {
  return (
    <div className="relative animate-fade-up [animation-delay:120ms] mx-auto w-full max-w-[520px] lg:mt-2">
      {/* Stage-view preview — dark surface, the one allowed dark moment because this shows the projection artefact */}
      <div className="relative bg-rc-ink text-rc-paper rounded-rc-xl p-6 sm:p-7 shadow-cardLg">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2.5">
            <span className="relative inline-flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-rc-lime opacity-75 animate-shimmer" />
              <span className="relative inline-flex w-2 h-2 rounded-full bg-rc-lime" />
            </span>
            <span className="text-eyebrow font-semibold uppercase text-rc-lime">
              Live · 47 Stimmen
            </span>
          </div>
          <span className="inline-flex items-center gap-1 rounded-pill border-[1.5px] border-rc-stone/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-rc-stone">
            Stage-Ansicht
          </span>
        </div>

        <p className="text-[20px] sm:text-[22px] font-semibold leading-[1.2] tracking-[-0.01em] text-rc-paper mb-6">
          Welche Aufgabe würde KI heute schon besser machen als ihr?
        </p>

        {/* The live cluster grid — three clusters with dot clouds (the upcoming flight-path target) */}
        <div className="grid grid-cols-2 gap-2.5 mb-5">
          <ClusterTile
            label="Spec schreiben"
            count={21}
            accent="berry"
            dots={14}
          />
          <ClusterTile
            label="Recherche verdichten"
            count={12}
            accent="paper"
            dots={9}
          />
          <ClusterTile
            label="UI-Copy variieren"
            count={8}
            accent="paper"
            dots={6}
          />
          <ClusterTile
            label="Außerhalb"
            count={2}
            accent="muted"
            dots={2}
          />
        </div>

        {/* In-flight response chip — foreshadows the upcoming flight-path animation, static here */}
        <div className="relative mb-5 h-[44px]">
          <div className="absolute left-0 top-0 rounded-rc-lg bg-rc-paper/[0.06] border border-rc-graphite px-3.5 py-2 text-[13px] text-rc-paper/85 leading-[1.35] max-w-[60%]">
            Wettbewerbsanalysen verdichten.
          </div>
          <svg
            aria-hidden
            className="absolute left-[60%] top-1/2 -translate-y-1/2 h-6 w-[34%] text-rc-lime/60"
            viewBox="0 0 100 24"
            fill="none"
            preserveAspectRatio="none"
          >
            <path d="M 0 14 Q 50 -6 100 8" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3" />
            <circle cx="100" cy="8" r="2" fill="#C6F430" />
          </svg>
        </div>

        {/* Insight strip — Blind Spot as the photogenic AI moment */}
        <div className="border-t border-rc-graphite pt-5 flex items-start gap-3">
          <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full bg-rc-lime text-rc-black text-[12px] font-bold">
            BS
          </span>
          <div className="min-w-0">
            <div className="text-eyebrow font-semibold uppercase text-rc-lime mb-1">
              Blind Spot
            </div>
            <div className="text-[14px] sm:text-[15px] leading-[1.4] text-rc-paper">
              Niemand nennt Rechtstexte, obwohl ihr im Healthcare-Sektor arbeitet.
            </div>
          </div>
        </div>
      </div>

      {/* Störer sticker overlay */}
      <img
        src="/brand/stoerer-no-prompting.svg"
        alt=""
        aria-hidden
        className="hidden sm:block absolute -top-7 -right-6 w-[180px] pointer-events-none select-none"
        style={{ transform: "rotate(-8deg)", filter: "drop-shadow(4px 4px 0 #000)" }}
      />
    </div>
  );
}

function ClusterTile({
  label,
  count,
  accent,
  dots,
}: {
  label: string;
  count: number;
  accent: "berry" | "paper" | "muted";
  dots: number;
}) {
  const surface =
    accent === "berry"
      ? "bg-rc-berry/15 border-rc-berry/50"
      : accent === "muted"
      ? "bg-transparent border-rc-graphite border-dashed"
      : "bg-rc-paper/[0.04] border-rc-graphite";
  const labelColor = accent === "muted" ? "text-rc-stone" : "text-rc-paper";
  const countColor =
    accent === "berry"
      ? "text-rc-paper"
      : accent === "muted"
      ? "text-rc-stone"
      : "text-rc-paper/80";
  const dotColor =
    accent === "berry"
      ? "bg-rc-berry"
      : accent === "muted"
      ? "bg-rc-stone/50"
      : "bg-rc-paper/60";
  const visibleDots = Math.min(dots, 12);

  return (
    <div className={`rounded-rc-lg border ${surface} px-3 py-2.5`}>
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <div className={`text-[13px] font-semibold leading-tight ${labelColor} truncate`}>
          {label}
        </div>
        <div className={`text-[11px] font-semibold tabular-nums ${countColor}`}>{count}</div>
      </div>
      <div className="flex flex-wrap gap-[3px]">
        {Array.from({ length: visibleDots }).map((_, i) => (
          <span key={i} className={`w-[6px] h-[6px] rounded-full ${dotColor}`} aria-hidden />
        ))}
      </div>
    </div>
  );
}

function Vocabulary() {
  return (
    <section id="vokabular" className="bg-rc-cream border-t border-rc-fog">
      <div className="max-w-marketing mx-auto px-6 sm:px-8 py-20 sm:py-24">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16 items-start">
          <div className="lg:sticky lg:top-24">
            <div className="text-eyebrow font-semibold uppercase text-rc-charcoal mb-5">
              Das Vokabular
            </div>
            <h2 className="font-display font-bold text-display-md text-rc-black tracking-display mb-5 text-balance">
              Fünf Begriffe.<br />
              <span className="text-rc-berry">Den Rest macht der Raum.</span>
            </h2>
            <p className="text-[16px] sm:text-[17px] leading-[1.6] text-rc-charcoal max-w-[28rem]">
              Was die KI für euch sieht. Keine Diagramme, keine Statistik. Konkrete Beobachtungen,
              die ein guter zweiter Moderator in die Runde gibt.
            </p>
          </div>

          <ol className="flex flex-col gap-3">
            <VocabularyRow
              ordinal="01"
              term="Cluster"
              meaning="Antworten verdichten sich live zu benannten Themen. Neue Stimmen finden ihren Platz, während ihr noch lest."
              tone="berry"
            />
            <VocabularyRow
              ordinal="02"
              term="Outlier"
              meaning="Eine Stimme, die nirgends passt, aber wichtig ist. Begründet, nicht ausgeblendet."
              tone="paper"
            />
            <VocabularyRow
              ordinal="03"
              term="Blind Spot"
              meaning="Was niemand gesagt hat, obwohl es eigentlich auftauchen müsste. Die KI nennt es laut."
              tone="ink"
            />
            <VocabularyRow
              ordinal="04"
              term="Kritische Frage"
              meaning="Die schärfere Rückfrage an den Raum, gegründet auf dem, was wirklich gesagt wurde."
              tone="paper"
            />
            <VocabularyRow
              ordinal="05"
              term="Manifest"
              meaning="Am Ende der Session: ein teilbares Artefakt, das den Konsens des Raums festhält."
              tone="paper"
            />
          </ol>
        </div>
      </div>
    </section>
  );
}

function VocabularyRow({
  ordinal,
  term,
  meaning,
  tone,
}: {
  ordinal: string;
  term: string;
  meaning: string;
  tone: "berry" | "paper" | "ink";
}) {
  const surface =
    tone === "ink"
      ? "bg-rc-ink text-rc-paper border-rc-ink"
      : tone === "berry"
      ? "bg-rc-paper text-rc-black border-rc-fog"
      : "bg-rc-paper text-rc-black border-rc-fog";
  const ordinalColor =
    tone === "ink"
      ? "text-rc-lime"
      : tone === "berry"
      ? "text-rc-berry"
      : "text-rc-stone";
  const termColor =
    tone === "ink" ? "text-rc-paper" : tone === "berry" ? "text-rc-berry" : "text-rc-black";
  const meaningColor = tone === "ink" ? "text-rc-paper/80" : "text-rc-charcoal";

  return (
    <li
      className={`relative rounded-rc-lg border-[1.5px] ${surface} px-5 sm:px-7 py-5 sm:py-6 grid grid-cols-[auto_1fr] gap-x-5 sm:gap-x-7 gap-y-1 items-baseline`}
    >
      <div
        className={`font-display text-[26px] sm:text-[30px] font-bold tabular-nums leading-none tracking-[-0.02em] ${ordinalColor}`}
      >
        {ordinal}
      </div>
      <div
        className={`font-display text-[22px] sm:text-[26px] font-bold leading-[1.1] tracking-[-0.015em] ${termColor}`}
      >
        {term}
      </div>
      <div aria-hidden />
      <div className={`text-[15px] sm:text-[16px] leading-[1.55] ${meaningColor} max-w-[36rem]`}>
        {meaning}
      </div>
    </li>
  );
}

function Contrast() {
  return (
    <section className="bg-rc-paper border-t border-rc-fog">
      <div className="max-w-marketing mx-auto px-6 sm:px-8 py-20 sm:py-24">
        <div className="max-w-2xl mb-12 sm:mb-14">
          <div className="text-eyebrow font-semibold uppercase text-rc-charcoal mb-5">
            Mehr als Polls
          </div>
          <h2 className="font-display font-bold text-display-md text-rc-black tracking-display text-balance">
            Wo andere Tools beim Abzählen aufhören,<br />
            <span className="text-rc-berry">fängt REASONZ an.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-rc-fog rounded-rc-lg overflow-hidden border-[1.5px] border-rc-fog">
          <ContrastColumn
            from="Antworten zählen."
            to="Antworten verstehen."
            detail="Die KI clustert kontinuierlich. Jede neue Stimme verändert das Bild, das der Raum von sich selbst sieht."
          />
          <ContrastColumn
            from="Ergebnisse beschreiben."
            to="Den Raum herausfordern."
            detail="Outlier, Blind Spot, Kritische Frage. Drei Interventionen, die eine Moderation in den nächsten Schritt drücken."
          />
          <ContrastColumn
            from="Ein Widget im Foliendeck."
            to="Ein Artefakt für den Raum."
            detail="Die Stage-Ansicht ist für die Wand gebaut, nicht für die Folie. Am Ende bleibt ein Manifest, kein Screenshot."
          />
        </div>
      </div>
    </section>
  );
}

function ContrastColumn({
  from,
  to,
  detail,
}: {
  from: string;
  to: string;
  detail: string;
}) {
  return (
    <div className="bg-rc-paper p-7 sm:p-8 flex flex-col">
      <div className="text-[14px] text-rc-slate leading-[1.5] line-through decoration-rc-stone/60 decoration-1 mb-2.5">
        {from}
      </div>
      <div className="font-display text-[22px] sm:text-[24px] font-bold leading-[1.15] tracking-[-0.015em] text-rc-black mb-4">
        {to}
      </div>
      <div className="h-px bg-rc-fog mb-4" />
      <p className="text-[15px] leading-[1.55] text-rc-charcoal">{detail}</p>
    </div>
  );
}

function UseCases() {
  return (
    <section id="raeume" className="bg-rc-cream border-t border-rc-fog">
      <div className="max-w-marketing mx-auto px-6 sm:px-8 py-20 sm:py-24">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12 sm:mb-14 max-w-4xl">
          <div>
            <div className="text-eyebrow font-semibold uppercase text-rc-charcoal mb-5">
              Drei Räume
            </div>
            <h2 className="font-display font-bold text-display-md text-rc-black tracking-display text-balance">
              Vom Strategietag<br />
              <span className="text-rc-berry">bis zum Bürgerdialog.</span>
            </h2>
          </div>
          <p className="text-[16px] leading-[1.55] text-rc-charcoal max-w-[22rem]">
            REASONZ läuft in Räumen, in denen Polling-Tools an die Decke stoßen, weil sie nur zählen können.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-5 lg:gap-6">
          <UseCaseCard
            ordinal="01"
            kicker="Facilitiert"
            title="Workshops & Strategietage"
            body="Kleine bis mittlere Räume. Die Moderation öffnet Fragen, schließt sie, holt sich Blind Spots und kritische Fragen, wenn sie sie braucht."
            traits={["Manuelle Kontrolle", "Drei Insight-Typen", "Post-Session Manifest"]}
            span="lg:col-span-5"
            tone="paper"
          />
          <UseCaseCard
            ordinal="02"
            kicker="Selbstläufer"
            title="Großevents & Konferenzen"
            body="Hunderte Teilnehmende, kein Mensch an den Reglern. Fragen rotieren im Survey-Modus, die Stage-Ansicht im Saal zeigt das Denken des Raums in Echtzeit."
            traits={["Auto-Rotation", "Auto-Insights", "Live-Hallenbildschirm"]}
            span="lg:col-span-7"
            tone="ink"
          />
          <UseCaseCard
            ordinal="03"
            kicker="Öffentlich"
            title="Bürgerdialog & Beteiligung"
            body="Town-Hall-Formate, Stadtteildebatten, Konsultationen. Antworten lassen sich nach Stream oder Bezirk taggen. Eine Heatmap zeigt, woher welche Stimme kommt."
            traits={["Stream-Tagging", "Stadtteil-Heatmap", "Skaliert auf Bürgermaßstab"]}
            span="lg:col-span-12"
            tone="paper-wide"
          />
        </div>
      </div>
    </section>
  );
}

function UseCaseCard({
  ordinal,
  kicker,
  title,
  body,
  traits,
  span,
  tone,
}: {
  ordinal: string;
  kicker: string;
  title: string;
  body: string;
  traits: string[];
  span: string;
  tone: "paper" | "ink" | "paper-wide";
}) {
  const isInk = tone === "ink";
  const surface = isInk
    ? "bg-rc-ink text-rc-paper"
    : "bg-rc-paper text-rc-black border-[1.5px] border-rc-fog";
  const kickerColor = isInk ? "text-rc-lime" : "text-rc-berry";
  const ordinalColor = isInk ? "text-rc-stone" : "text-rc-stone";
  const bodyColor = isInk ? "text-rc-paper/80" : "text-rc-charcoal";
  const traitBorder = isInk
    ? "border-rc-graphite text-rc-paper/85"
    : "border-rc-fog text-rc-charcoal";
  const traitDot = isInk ? "bg-rc-lime" : "bg-rc-berry";
  const isWide = tone === "paper-wide";

  return (
    <article
      className={`${span} ${surface} rounded-rc-xl p-7 sm:p-9 flex ${
        isWide ? "flex-col lg:flex-row lg:items-start lg:gap-12" : "flex-col"
      }`}
    >
      <div className={isWide ? "lg:max-w-[24rem] lg:flex-shrink-0" : ""}>
        <div className="flex items-baseline justify-between mb-4">
          <div className={`text-eyebrow font-semibold uppercase ${kickerColor}`}>{kicker}</div>
          <div className={`font-display text-[18px] font-bold tabular-nums tracking-[-0.01em] ${ordinalColor}`}>
            {ordinal}
          </div>
        </div>
        <h3
          className={`font-display font-bold text-[26px] sm:text-[30px] leading-[1.1] tracking-[-0.015em] mb-4 ${
            isInk ? "text-rc-paper" : "text-rc-black"
          }`}
        >
          {title}
        </h3>
        {!isWide && (
          <p className={`text-[15px] sm:text-[16px] leading-[1.6] ${bodyColor} mb-6`}>{body}</p>
        )}
      </div>

      <div className={isWide ? "lg:flex-1 lg:pt-1" : ""}>
        {isWide && (
          <p className={`text-[15px] sm:text-[16px] leading-[1.6] ${bodyColor} mb-6 max-w-[44rem]`}>
            {body}
          </p>
        )}
        <ul className={`flex flex-wrap gap-2 ${isWide ? "" : "mt-auto"}`}>
          {traits.map((t) => (
            <li
              key={t}
              className={`inline-flex items-center gap-2 rounded-pill border ${traitBorder} px-3 py-1.5 text-[13px] font-medium`}
            >
              <span aria-hidden className={`inline-block w-1.5 h-1.5 rounded-full ${traitDot}`} />
              {t}
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}

function Paths() {
  return (
    <section id="paths" className="bg-rc-paper border-y border-rc-fog">
      <div className="max-w-marketing mx-auto px-6 sm:px-8 py-20 sm:py-24">
        <div className="max-w-2xl mb-12 sm:mb-14">
          <div className="text-eyebrow font-semibold uppercase text-rc-charcoal mb-5">
            So findet ihr eure Rolle
          </div>
          <h2 className="font-display font-bold text-display-md text-rc-black tracking-display">
            Zwei Wege in den Raum.
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          <FacilitatorCard />
          <ParticipantCard />
        </div>
      </div>
    </section>
  );
}

function FacilitatorCard() {
  return (
    <div className="relative rounded-rc-xl bg-rc-cream border-[1.5px] border-rc-fog p-8 sm:p-10 flex flex-col">
      <div className="text-eyebrow font-semibold uppercase text-rc-berry mb-5">
        Facilitator
      </div>
      <h3 className="font-display font-bold text-[28px] sm:text-[32px] leading-[1.1] tracking-[-0.015em] text-rc-black mb-4">
        Eure Events steuern.
      </h3>
      <p className="text-[16px] leading-[1.6] text-rc-charcoal mb-8 max-w-[28rem]">
        Fragen vorbereiten, live öffnen und schließen, Clustering anstoßen, Outlier und blinde
        Flecken sichtbar machen, Antworten auf die Bühne pinnen.
        <strong className="font-semibold text-rc-black"> Alles in einer Konsole.</strong>
      </p>
      <ul className="space-y-2 mb-10 text-[15px] text-rc-charcoal">
        <FacilitatorBullet>Events mit kurzem Join-Code anlegen</FacilitatorBullet>
        <FacilitatorBullet>Survey-Modus oder facilitiertes Format</FacilitatorBullet>
        <FacilitatorBullet>Stage-Ansicht für den großen Bildschirm</FacilitatorBullet>
      </ul>
      <a
        href="/facilitator"
        className="mt-auto inline-flex w-fit items-center gap-2 bg-rc-berry text-rc-paper rounded-pill px-7 py-3.5 text-[15px] font-semibold hover:bg-rc-berry-700 active:scale-[0.97] transition ease-rc-out"
      >
        Zur Console <span aria-hidden>→</span>
      </a>
    </div>
  );
}

function FacilitatorBullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <span aria-hidden className="mt-[10px] inline-block w-[6px] h-[6px] rounded-full bg-rc-berry flex-shrink-0" />
      <span>{children}</span>
    </li>
  );
}

function ParticipantCard() {
  const [code, setCode] = useState("");
  const trimmed = code.trim().toUpperCase();
  const valid = /^[A-Z0-9]{4,6}$/.test(trimmed);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    window.location.href = `/join/${trimmed}`;
  };

  return (
    <div className="relative rounded-rc-xl bg-rc-ink text-rc-paper p-8 sm:p-10 flex flex-col overflow-hidden">
      <div className="text-eyebrow font-semibold uppercase text-rc-lime mb-5">
        Teilnehmen
      </div>
      <h3 className="font-display font-bold text-[28px] sm:text-[32px] leading-[1.1] tracking-[-0.015em] text-rc-paper mb-4">
        Mit Code beitreten.
      </h3>
      <p className="text-[16px] leading-[1.6] text-rc-paper/70 mb-8 max-w-[28rem]">
        Kein Account, keine App. Code von eurer Moderation eingeben oder den QR-Code auf der Bühne
        scannen. <strong className="font-semibold text-rc-paper">Antworten bleiben anonym.</strong>
      </p>

      <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 mb-3" noValidate>
        <label htmlFor="join-code" className="sr-only">Event-Code</label>
        <input
          id="join-code"
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 6))}
          placeholder="XK9F2"
          maxLength={6}
          aria-invalid={code.length > 0 && !valid}
          className="flex-1 bg-rc-paper/[0.06] border-[1.5px] border-rc-graphite rounded-rc-lg px-5 py-3.5 text-[18px] font-semibold tracking-[0.18em] text-rc-paper placeholder:text-rc-stone/50 placeholder:font-medium placeholder:tracking-[0.18em] focus:outline-none focus:border-rc-lime focus:bg-rc-paper/[0.08] transition uppercase"
        />
        <button
          type="submit"
          disabled={!valid}
          className="inline-flex items-center justify-center gap-2 bg-rc-lime text-rc-black rounded-pill px-7 py-3.5 text-[15px] font-bold hover:bg-rc-lime-700 active:scale-[0.97] disabled:bg-rc-graphite disabled:text-rc-stone disabled:cursor-not-allowed transition ease-rc-out"
        >
          Beitreten <span aria-hidden>→</span>
        </button>
      </form>
      <p className="text-[13px] text-rc-stone leading-[1.5]">
        4–6 Zeichen, Buchstaben oder Zahlen. Beispiel: <span className="font-semibold text-rc-paper/80 tracking-[0.18em]">XK9F2</span>.
      </p>
    </div>
  );
}

function Pricing() {
  return (
    <section id="preise" className="bg-rc-paper border-t border-rc-fog">
      <div className="max-w-marketing mx-auto px-6 sm:px-8 py-20 sm:py-24">
        <div className="max-w-2xl mb-12 sm:mb-14">
          <div className="text-eyebrow font-semibold uppercase text-rc-charcoal mb-5">
            Preise
          </div>
          <h2 className="font-display font-bold text-display-md text-rc-black tracking-display">
            Kostenlos starten. <span className="text-rc-berry">Pro kommt bald.</span>
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.6] text-rc-charcoal mt-5 max-w-[36rem]">
            Der volle KI-Co-Moderator, sofort und ohne Kosten. Wenn eure Events wachsen,
            wächst REASONZ mit. <strong className="font-semibold text-rc-black">Bezahlpläne kommen bald.</strong>
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
          {/* Free — available now */}
          <div className="relative rounded-rc-xl bg-rc-cream border-[1.5px] border-rc-fog p-8 sm:p-10 flex flex-col">
            <div className="flex items-center gap-3 mb-5">
              <span className="text-eyebrow font-semibold uppercase text-rc-berry">Free</span>
              <span className="inline-flex items-center rounded-pill bg-rc-lime/25 border border-rc-lime/40 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-rc-black">
                Jetzt verfügbar
              </span>
            </div>
            <div className="flex items-baseline gap-2 mb-4">
              <span className="font-display font-bold text-[44px] leading-none tracking-[-0.02em] text-rc-black">0 €</span>
              <span className="text-[15px] text-rc-charcoal">für immer</span>
            </div>
            <p className="text-[16px] leading-[1.6] text-rc-charcoal mb-8 max-w-[28rem]">
              Der volle KI-Co-Moderator für dein erstes Event.
              <strong className="font-semibold text-rc-black"> Für Teilnehmende ohne Account, ohne App.</strong>
            </p>
            <ul className="space-y-2 mb-10 text-[15px] text-rc-charcoal">
              <PriceBullet accent="berry">1 aktives Event gleichzeitig</PriceBullet>
              <PriceBullet accent="berry">Bis zu 40 Teilnehmende pro Event</PriceBullet>
              <PriceBullet accent="berry">KI-Clustering & Insights in Echtzeit</PriceBullet>
              <PriceBullet accent="berry">Content-Moderation & Stage-Ansicht</PriceBullet>
            </ul>
            <a
              href="/facilitator"
              className="mt-auto inline-flex w-fit items-center gap-2 bg-rc-berry text-rc-paper rounded-pill px-7 py-3.5 text-[15px] font-semibold hover:bg-rc-berry-700 active:scale-[0.97] transition ease-rc-out"
            >
              Kostenlos starten <span aria-hidden>→</span>
            </a>
          </div>

          {/* Pro — coming soon */}
          <div className="relative rounded-rc-xl bg-rc-ink text-rc-paper p-8 sm:p-10 flex flex-col overflow-hidden">
            <span className="absolute top-7 right-7 rotate-[5deg] bg-rc-lime text-rc-black text-[11px] font-bold uppercase tracking-[0.14em] px-3 py-1 rounded-pill shadow-hard">
              Bald
            </span>
            <div className="text-eyebrow font-semibold uppercase text-rc-lime mb-5">Pro</div>
            <div className="mb-4">
              <span className="font-display font-bold text-[36px] leading-none tracking-[-0.02em] text-rc-paper">Bald verfügbar</span>
            </div>
            <p className="text-[16px] leading-[1.6] text-rc-paper/70 mb-8 max-w-[28rem]">
              Für Teams, die mehr Räume öffnen und größere Events fahren.
              <strong className="font-semibold text-rc-paper"> Wir melden uns, sobald es losgeht.</strong>
            </p>
            <ul className="space-y-2 mb-10 text-[15px] text-rc-paper/80">
              <PriceBullet accent="lime">Mehrere Events gleichzeitig</PriceBullet>
              <PriceBullet accent="lime">Größere Räume, deutlich mehr Teilnehmende</PriceBullet>
              <PriceBullet accent="lime">Mehr KI-Läufe pro Event</PriceBullet>
              <PriceBullet accent="lime">Prioritäts-Support</PriceBullet>
            </ul>
            <span className="mt-auto inline-flex w-fit items-center gap-2 bg-rc-graphite text-rc-stone rounded-pill px-7 py-3.5 text-[15px] font-semibold cursor-default select-none">
              In Vorbereitung
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function PriceBullet({ children, accent }: { children: React.ReactNode; accent: "berry" | "lime" }) {
  return (
    <li className="flex items-start gap-2.5">
      <span
        aria-hidden
        className={`mt-[9px] inline-block w-[6px] h-[6px] rounded-full flex-shrink-0 ${accent === "berry" ? "bg-rc-berry" : "bg-rc-lime"}`}
      />
      <span>{children}</span>
    </li>
  );
}

function SiteFooter() {
  return (
    <footer className="bg-rc-black text-rc-paper">
      <div className="max-w-marketing mx-auto px-6 sm:px-8 py-14 sm:py-16">
        <div className="grid sm:grid-cols-[1.6fr_1fr_1fr] gap-10 sm:gap-12">
          <div>
            <div className="font-display text-[28px] font-bold tracking-[-0.02em] text-rc-paper mb-1">
              REASONZ<span className="text-rc-berry">.</span>
            </div>
            <p className="text-[14px] text-rc-stone leading-[1.55] max-w-[20rem] mb-5">
              Der KI-Co-Moderator für Workshops, Großevents und Bürgerdialog. Auf Deutsch.
            </p>
            <a
              href="https://readycal.de"
              className="inline-flex items-center gap-2 text-[12px] text-rc-stone hover:text-rc-paper transition-colors"
            >
              <img src="/brand/readycal-logo-negativ.png" alt="" className="h-4 w-auto block opacity-80" />
              <span>by READYCAL</span>
            </a>
          </div>
          <FooterCol
            title="Produkt"
            items={[
              { label: "Was die KI sieht", href: "#vokabular" },
              { label: "Drei Räume", href: "#raeume" },
              { label: "Für Facilitator", href: "/facilitator" },
              { label: "Beitreten", href: "#paths" },
            ]}
          />
          <FooterCol
            title="Kontakt"
            muted
            items={[
              { label: "info@readycal.de", href: "mailto:info@readycal.de" },
              { label: "Hannover, Deutschland", href: null },
            ]}
          />
        </div>
        <div className="mt-12 pt-6 border-t border-rc-charcoal flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-[12px] text-rc-stone">
          <div>© {new Date().getFullYear()} REASONZ · ein Produkt von READYCAL, Hannover.</div>
          <div className="flex gap-6">
            <a href="/datenschutz" className="hover:text-rc-paper transition-colors">Datenschutz</a>
            <a href="/impressum" className="hover:text-rc-paper transition-colors">Impressum</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

type FooterItem = { label: string; href: string | null };

function FooterCol({
  title,
  items,
  muted = false,
}: {
  title: string;
  items: FooterItem[];
  muted?: boolean;
}) {
  return (
    <div>
      <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-rc-lime mb-4">
        {title}
      </div>
      <ul className="flex flex-col gap-2 text-[14px]">
        {items.map((it) => (
          <li key={it.label}>
            {it.href ? (
              <a
                href={it.href}
                className={`${muted ? "text-rc-stone" : "text-rc-paper"} hover:text-rc-lime transition-colors`}
              >
                {it.label}
              </a>
            ) : (
              <span className={muted ? "text-rc-stone" : "text-rc-paper"}>{it.label}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
